import { Euler, Vector3 } from "three";

// Precision in the project is 0.01 (1/100), so rounding at the tenth of this precision
const PRECISION = 1000;
const EPSILON = 1 / PRECISION;
const round: (n: number) => number = n => Math.round(n * PRECISION) / PRECISION;
const roundVect: (v: Vector3) => Vector3 = v =>
	v.multiplyScalar(PRECISION).round().divideScalar(PRECISION);
export const lowerThan: (f1: number, f2: number) => boolean = (f1, f2) => f2 - f1 > EPSILON;
export const upperThan: (f1: number, f2: number) => boolean = (f1, f2) => f1 - f2 > EPSILON;

const HALF_PI = Math.PI / 2;

// Method to compute the "real" position of the Box regarding its dimensions and rotations.
// (0,0,0) of the box is the center of the box, the idea here is to make (0,0,0) the bottom left back corner of the entity
const computePosition = (entity: ElementDefinitionType, position: ElementPositionType): Vector => {
	const pos = computePositionNoRotation(
		{ position: new Vector3(0, 0, 0), dimensions: entity.getBoundingBox() },
		entity,
		position
	);
	return [pos.x + pos.width / 2, pos.y + pos.height / 2, pos.z + pos.depth / 2];
};

const getBottomLeftBackCorner: (v: Vector3) => Vector3 = v =>
	new Vector3(-Math.abs(v.x) / 2, -Math.abs(v.y) / 2, -Math.abs(v.z) / 2);

// Computes the position of the element when there is no rotation on it
// The (0,0,0) position is already the bottom left back corner of the entity
// Note: the bottom left back corner is (lowest x, lowest y and lowest z)
// as a result, width, height and depth dimensions are always positive
// TODO: to fix, bottom anchor should be relative to the object... We should ALSO apply a translation if we really want it...
// It would simplify the rotation management ?
const computePositionNoRotation: (
	part: PartDefinition,
	anchor: ElementDefinitionType,
	anchorPosition?: ElementPositionType
) => ElementPositionNoRotationType = (part, anchor, anchorPosition = {}) => {
	const { rotX = 0, rotY = 0, rotZ = 0, position = [0, 0, 0] } = anchorPosition;
	const anchorVect = new Vector3(...anchor.getBoundingBox());

	const rotation = new Euler(HALF_PI * rotX, HALF_PI * rotY, HALF_PI * rotZ);

	// Part dimensions rotated
	const partVect = new Vector3().fromArray(part.dimensions);
	let { x: width, y: height, z: depth } = roundVect(partVect.clone().applyEuler(rotation));

	// Part position rotated.
	// Computed from relative position in element,
	// translated to BLB for part
	// and substract BLB of element to get absolute BLB for this part
	const partPos = part.position
		.clone()
		.add(getBottomLeftBackCorner(partVect))
		.sub(getBottomLeftBackCorner(anchorVect))
		.applyEuler(rotation);

	// Part final position is
	// Bottom Left Back corner translation on the anchor
	// (which is original BLB corner rotated - new BLB corner)
	// + anchor position
	// + part position rotated
	let { x, y, z } = roundVect(
		getBottomLeftBackCorner(anchorVect)
			.applyEuler(rotation)
			.sub(getBottomLeftBackCorner(anchorVect.applyEuler(rotation)))
			.add(new Vector3().fromArray(position))
			.add(partPos)
	);

	// Standardize dimensions to positive
	if (width < 0) {
		x = round(x + width);
		width = -width;
	}
	if (height < 0) {
		y = round(y + height);
		height = -height;
	}
	if (depth < 0) {
		z = round(z + depth);
		depth = -depth;
	}

	return { x, y, z, width, height, depth };
};

function getMinMax(x: number, width: number) {
	return [x, round(x + width)];
}
function baseIntersect(
	b1: ElementPositionNoRotationType,
	b2: ElementPositionNoRotationType,
	axis: AxisEnum
) {
	// For rotated rectangles: https://stackoverflow.com/a/29614525
	// Here, we manage only rectangles without rotation

	const [minAx, maxAx] =
		axis === "y" || axis === "z" ? getMinMax(b1.x, b1.width) : getMinMax(b1.z, b1.depth);
	const [minBx, maxBx] =
		axis === "y" || axis === "z" ? getMinMax(b2.x, b2.width) : getMinMax(b2.z, b2.depth);

	const [minAz, maxAz] = axis === "y" ? getMinMax(b1.z, b1.depth) : getMinMax(b1.y, b1.height);
	const [minBz, maxBz] = axis === "y" ? getMinMax(b2.z, b2.depth) : getMinMax(b2.y, b2.height);

	return (
		lowerThan(minBx, maxAx) &&
		lowerThan(minAx, maxBx) &&
		lowerThan(minAz, maxBz) &&
		lowerThan(minBz, maxAz)
	);
}

export { computePosition, computePositionNoRotation, baseIntersect, round };
